import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse, PageQuery } from '../types/api.types';
import { CancellationRefund, Rental, RentalStatus } from '../types/rental.types';

@Injectable({ providedIn: 'root' })
export class RentalsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/rental/api/rentals`;

  list(query: PageQuery = {}): Observable<ApiPageResponse<Rental>> {
    return this.http.get<ApiPageResponse<Rental>>(this.base, {
      params: this.pageParams(query),
    });
  }

  /** Self-service : locations du user courant (locataire ou propriétaire
   *  d'un véhicule loué). Même contrat paginé que `list()`. */
  listMine(query: PageQuery = {}): Observable<ApiPageResponse<Rental>> {
    return this.http.get<ApiPageResponse<Rental>>(`${this.base}/me`, {
      params: this.pageParams(query),
    });
  }

  get(id: number): Observable<Rental> {
    return this.http.get<Rental>(`${this.base}/${id}`);
  }

  /** Détail self-service. */
  getMine(id: number): Observable<Rental> {
    return this.http.get<Rental>(`${this.base}/me/${id}`);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  /** Suppression self-service. */
  deleteMine(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/me/${id}`);
  }

  /** Change le statut d'une location dont je suis partie prenante.
   *  Côté owner : accepter (CONFIRMED), refuser/annuler (CANCELLED), terminer (FINISHED). */
  changeStatusMine(id: number, status: RentalStatus): Observable<Rental> {
    return this.http.get<Rental>(`${this.base}/changeStatusMe/${id}/${status}`);
  }

  /** Démarrage manuel (secours sans scan) — `POST /rental/api/rentals/{id}/start`. 409 sans photo « avant » du client. */
  start(id: number): Observable<Rental> {
    return this.http.post<Rental>(`${this.base}/${id}/start`, {});
  }

  /** Démarrage par scan du QR de retrait présenté par le client — `POST /rental/api/rentals/start-by-qr`. */
  startByQr(qrPayload: string): Observable<Rental> {
    return this.http.post<Rental>(`${this.base}/start-by-qr`, { qrPayload });
  }

  /** Fin par scan du QR de restitution présenté par le client — `POST /rental/api/rentals/finish-by-qr`. 409 sans photo « après ». */
  finishByQr(qrPayload: string): Observable<Rental> {
    return this.http.post<Rental>(`${this.base}/finish-by-qr`, { qrPayload });
  }

  /** Fin manuelle (secours sans scan) — `POST /rental/api/rentals/{id}/finish`. 409 sans photo « après ». */
  finish(id: number): Observable<Rental> {
    return this.http.post<Rental>(`${this.base}/${id}/finish`, {});
  }

  /** Photos d'état des lieux de restitution (loueur, location en cours) — `POST /rental/api/rentals/{id}/photos/after`. */
  uploadAfterPhotos(id: number, files: File[]): Observable<unknown[]> {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    return this.http.post<unknown[]>(`${this.base}/${id}/photos/after`, fd);
  }

  /** Annulation d'une location (avant STARTED). Le back calcule et renvoie le
   *  **détail de remboursement** (montant/%, frais, politique appliquée) — pas
   *  un `Rental`. Cf. `CancellationRefund` + `docs/CANCELLATION_POLICY_STATUS.md`. */
  cancel(id: number): Observable<CancellationRefund> {
    return this.http.post<CancellationRefund>(`${this.base}/${id}/cancel`, {});
  }

  /** **Preview** du remboursement **sans annuler** (§B4) —
   *  `GET /rental/api/rentals/{id}/cancellation-preview`. Self-scoped ; `400` si la
   *  location n'est pas annulable. `warning` omis si le remboursement est > 0. */
  cancellationPreview(id: number): Observable<CancellationRefund> {
    return this.http.get<CancellationRefund>(`${this.base}/${id}/cancellation-preview`);
  }

  count(search?: string): Observable<number> {
    let params = new HttpParams();
    if (search?.trim()) params = params.set('search', search.trim());
    return this.http.get<number>(`${this.base}/count`, { params });
  }

  private pageParams(query: PageQuery): HttpParams {
    let params = new HttpParams()
      .set('pageable', 'true')
      .set('page', String(query.page ?? 1))
      .set('size', String(query.size ?? 10))
      .set('isDeleted', String(query.isDeleted ?? false));
    if (query.search?.trim()) params = params.set('search', query.search.trim());
    return params;
  }
}
